import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import { FLOATING_KEYBOARD_ANIM_MS, useModalBackdropFade } from '../../utils/learnerSlideModalMotion';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';

const PANEL_ANIM_MS = FLOATING_KEYBOARD_ANIM_MS;

export interface LearnerAlertDialogProps {
  open: boolean;
  title: string;
  message: string;
  primaryLabel: string;
  secondaryLabel: string;
  onPrimary: () => void;
  onSecondary: () => void;
  direction?: string;
  titleId?: string;
  messageId?: string;
}

/**
 * Confirm dialog above learner slide modals (z-210).
 * Enter/exit motion matches NonFarsiContentHintModal (lesson-not-passed-hint-*).
 */
export const LearnerAlertDialog: React.FC<LearnerAlertDialogProps> = ({
  open,
  title,
  message,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  direction,
  titleId = 'learner-alert-dialog-title',
  messageId = 'learner-alert-dialog-message',
}) => {
  const isRtl = direction === 'rtl';
  const { t } = useTranslation('common');
  const [mounted, setMounted] = useState(false);
  const [exiting, setExiting] = useState(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setExiting(false);
      pendingActionRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  const finishExit = useCallback(() => {
    pendingActionRef.current?.();
    pendingActionRef.current = null;
    setMounted(false);
    setExiting(false);
  }, []);

  const startExit = useCallback(
    (action: () => void) => {
      if (exiting) return;
      pendingActionRef.current = action;
      if (prefersReducedMotion()) {
        finishExit();
        return;
      }
      setExiting(true);
      exitTimerRef.current = setTimeout(finishExit, PANEL_ANIM_MS);
    },
    [exiting, finishExit]
  );

  useEffect(() => {
    if (open || !mounted || exiting) return undefined;
    startExit(() => {});
    return undefined;
  }, [open, mounted, exiting, startExit]);

  useHistoryBackClose(open, () => startExit(onSecondary), 'learner-alert');

  const backdropVisible = useModalBackdropFade(mounted, exiting);

  if (!mounted) return null;

  const slideClass = exiting
    ? isRtl
      ? 'lesson-not-passed-hint-exit-rtl'
      : 'lesson-not-passed-hint-exit-ltr'
    : isRtl
      ? 'lesson-not-passed-hint-enter-rtl'
      : 'lesson-not-passed-hint-enter-ltr';

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center px-4 py-8" role="presentation">
      <div
        className={`keyboard-slide-modal-backdrop fixed inset-0 bg-black/55 backdrop-blur-[1px] pointer-events-auto${
          backdropVisible ? ' is-visible' : ''
        }`}
        aria-hidden="true"
        onClick={() => startExit(onPrimary)}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        className={`lesson-not-passed-hint-panel pointer-events-auto relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-gray-700 dark:bg-gray-900 ${slideClass}`}
        {...(direction ? { dir: direction } : {})}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => startExit(onPrimary)}
          className="absolute end-2 top-2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          aria-label={t('close')}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        <h3 id={titleId} className="mb-2 pe-6 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h3>
        <p id={messageId} className="mb-6 text-sm text-gray-600 dark:text-gray-400">
          {message}
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => startExit(onPrimary)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            onClick={() => startExit(onSecondary)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            {secondaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LearnerAlertDialog;
